import Select, {
  type GroupBase,
  type MenuPlacement,
  type OnChangeValue,
  type OptionsOrGroups,
  type SelectInstance,
  components,
} from 'react-select';
import { forwardRef, useMemo } from 'react';
import { startCase } from 'lodash';
import {
  ClearIndicator,
  CustomInput,
  CustomMultiValueRemove,
  DropdownIndicator,
} from '@/components/ui/react-select-elements';
import { shareDialogSelectClassNames } from '@/components/shares/share-dialog-select-class-names';
import { useInsideDialogContentElement } from '@/components/ui/inside-dialog-context';
import { type Option } from '@/types/select-option';
import { useTranslations } from 'next-intl';

interface MultiSelectProps {
  'name': string;
  'value': Array<string>;
  'options': OptionsOrGroups<Option, GroupBase<Option>>;
  'className'?: string;
  'placeholder'?: string;
  'isClearable'?: boolean;
  'isSearchable'?: boolean;
  'disabled'?: boolean;
  'isLoading'?: boolean;
  'onChange': (newValue: OnChangeValue<Option, true>) => void;
  'menuPlacement'?: MenuPlacement;
  'menuListStyle'?: string;
  'data-cy'?: string;
  'indeterminate'?: boolean;
  'aria-invalid'?: boolean | 'true' | 'false';
}

const CustomControl = (props: any) => {
  // Merge data-cy custom attribute into innerProps
  const newInnerProps = { ...props.innerProps, 'data-cy': props.selectProps['data-cy'] };
  return <components.Control {...props} innerProps={newInnerProps} />;
};

// A fixed menu stays put while its control scrolls away, so close it. Scrolling the option list is fine.
const isScrollOutsideMenu = (event: Event) =>
  !(event.target instanceof Element && event.target.closest('[role="listbox"]'));

export const MultiSelect = forwardRef<SelectInstance<Option, true>, MultiSelectProps>(
  (
    {
      name,
      value,
      options,
      className,
      placeholder = 'Select...',
      isClearable = true,
      isSearchable = true,
      disabled = false,
      isLoading = false,
      onChange,
      menuPlacement = 'auto',
      menuListStyle = '',
      indeterminate,
      'aria-invalid': ariaInvalid,
      ...props
    },
    ref,
  ) => {
    const tCommon = useTranslations('common');
    const dialogContentElement = useInsideDialogContentElement();
    // The app's portal layer (`pages/_app.tsx`), the same one the tags select uses.
    const portalTarget =
      dialogContentElement ?? (typeof document === 'undefined' ? null : document.querySelector('#portal'));
    const selectedOptions = useMemo(() => {
      const arrayValue = Array.isArray(value) ? value : [];

      return arrayValue
        ?.map((val) =>
          options.flatMap((opt) => ('options' in opt ? opt.options : [opt])).find((option) => option.value === val),
        )
        .filter((option) => option !== undefined);
    }, [value, options]);

    return (
      <Select
        ref={ref}
        // An inline menu gets cut off by any clipping ancestor, such as the metadata panel's
        // scroll area, so it goes to the app's portal layer instead wherever that is reachable.
        menuPortalTarget={portalTarget instanceof HTMLElement ? portalTarget : undefined}
        // react-select places the portaled menu in viewport coordinates, which only line up when
        // nothing between it and the viewport is a containing block.
        menuPosition="fixed"
        closeMenuOnScroll={isScrollOutsideMenu}
        menuPlacement={menuPlacement}
        isMulti
        value={selectedOptions}
        onChange={onChange}
        options={options}
        className={className}
        placeholder={
          indeterminate ? tCommon('multipleValuesSelected', { field: startCase(name.replace(/_/g, ' ')) }) : placeholder
        }
        isClearable={isClearable}
        isSearchable={isSearchable}
        isDisabled={disabled}
        aria-invalid={ariaInvalid}
        // Custom props read back via `selectProps` in shareDialogSelectClassNames.
        // Cast because react-select's exports block type augmentation under bundler resolution.
        {...({ indeterminate, menuListStyle } as any)}
        data-cy={props['data-cy']}
        components={{
          Input: CustomInput,
          Control: CustomControl,
          DropdownIndicator,
          ClearIndicator,
          MultiValueRemove: CustomMultiValueRemove,
        }}
        unstyled
        isLoading={isLoading}
        noOptionsMessage={() => tCommon('noOptionsFound')}
        classNames={shareDialogSelectClassNames as any}
      />
    );
  },
);

MultiSelect.displayName = 'MetadataMultiselect';
